import {Avatar, Box, Button, Chip, Drawer, Grid, IconButton, Stack, Tab, Tabs, Typography} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import moment from "moment";
import {useEffect, useState} from "react";
import XLSX from "xlsx";
import {
  BALANCE_RECORD_TYPES,
  BALANCE_RECORD_TYPE_MAP,
  IMG_BASE_URL,
  PAYMENT_STATUS,
  PAY_METHOD_MAP,
  POINT_CATEGORY_MAP,
  POINT_RECORD_TYPE_MAP,
  ROLE_LEVEL_MAP,
  TIEJUN_LEVEL_MAP,
  USER_LEVEL_MAP,
  USER_STATUS_MAP,
  XFQ_RECORD_TYPE_MAP,
} from "src/config/constants";
import {asyncRequest} from "src/services/network";
import SectionHeader from "../section-header/SectionHeader";
import UserInfo from "./UserInfo";
import MerchantInfo from "./MerchantInfo";
import CellImage from "./CellImage";
import ReferralTree from "./ReferralTree";
import AncestorLine from "./AncestorLine";
import dayjs from "dayjs";
import ConvertUtil from "src/utils/ConvertUtil";
import Label from "../Label";
import {CloseOutlined} from "@mui/icons-material";
import Decimal from "decimal.js"
import CircleIcon from "@mui/icons-material/Circle";

const storeOrderColumns = [
  {
    field: "orderSN",
    headerName: "订单编号",
    width: 200,
    // editable: true,
  },
  {
    field: "totalPrice",
    headerName: "总金额",
    type: "number",
    width: 90,
  },
  {
    field: "payPrice",
    headerName: "支付金额",
    type: "number",
    width: 90,
    sortable: false,
  },
  {
    field: "payMethod",
    headerName: "支付方式",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => PAY_METHOD_MAP[value],
  },
  {
    field: "productData",
    headerName: "商品信息",
    // description: 'This column has a value getter and is not sortable.',
    width: 190,
    renderCell: ({row}) => {
      return row.productData.map((p) => (
        <Stack direction="row" spacing={1}>
          <CellImage src={p.cover} style={{width: 60, height: 60, objectFit: "contain"}}/>
          <Stack>
            <Typography>{p.name}</Typography>
            <Typography>x{p.count}</Typography>
          </Stack>
        </Stack>
      ));
    },
  },
  {
    field: "status",
    headerName: "状态",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    renderCell: ({row}) => {
      return (
        <Label variant="ghost" color={ConvertUtil.getOrderStatusColor(row.status)}>
          {ConvertUtil.getOrderStatusText(row.status)}
        </Label>
      );
    },
  },
  {
    field: "createdAt",
    headerName: "创建时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
  {
    field: "payTime",
    headerName: "付款时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => (value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null),
  },
  {
    field: "uid",
    headerName: "鲸世优选UID",
    width: 120,
  },
];

const orderColumns = [
  // { field: 'id', headerName: 'ID', width: 90 },
  {
    field: "orderSN",
    headerName: "订单编号",
    width: 200,
    // editable: true,
  },
  {
    field: "totalPrice",
    headerName: "订单金额",
    align: "center",
    type: "number",
    width: 90,
    valueGetter: (value, row) => `￥${value}`,
  },
  {
    field: "rangli",
    headerName: "让利金额(比例)",
    align: "center",
    width: 120,
    valueGetter: (value, row) => `￥${row.rangli}(${new Decimal(value).div(row.totalPrice).mul(100).toFixed(0)}%)`,
  },
  {
    field: "detail",
    headerName: "商家应收",
    type: "number",
    align: "center",
    width: 90,
    valueGetter: (value, row) => `￥${new Decimal(row.totalPrice).sub(row.rangli).toNumber()}`,
  },
  {
    field: "sid",
    headerName: "支付信息(金额 | 消费券 | 积分)",
    type: "number",
    align: "center",
    width: 220,
    valueGetter: (value, row) => `￥${row.payPrice} | ${row.deductionXfqV2} | ${row.deductionPointsV2}`,
  },
  {
    field: "payMethod",
    headerName: "支付方式",
    // description: 'This column has a value getter and is not sortable.',
    align: "center",
    width: 140,
    valueGetter: (value, row) => (row.payPrice == 0 ? "消费券支付" : PAY_METHOD_MAP[value]),
  },
  {
    field: "merchant",
    headerName: "商户",
    // description: 'This column has a value getter and is not sortable.',
    width: 190,
    renderCell: ({row}) => {
      if (!row.merchant) return null;
      return <UserInfo uid={row.merchant.mid} avatar={row.merchant.logo} nickname={row.merchant.name}
                       enablePreview={false}/>;
    },
  },
  // {
  //   field: "rangli1",
  //   headerName: "让利",
  //   // description: 'This column has a value getter and is not sortable.',
  //   // width: 160,
  //   valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  // },
  // {
  //   field: "version",
  //   headerName: "版本",
  //   // description: 'This column has a value getter and is not sortable.',
  //   // width: 160,
  //   valueGetter: (value, row) => `${value}.0`,
  // },
  {
    field: "createdAt",
    headerName: "创建时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm"),
  },
  {
    field: "uid",
    headerName: "鲸世优选UID",
    width: 120,
  },
];

const pointColumns = [
  {field: "id", headerName: "ID", width: 90},
  {
    field: "category",
    headerName: "类别",
    width: 200,
    valueGetter: (value, row) => POINT_CATEGORY_MAP[value],
  },
  {
    field: "amount",
    headerName: "数额",
  },
  {
    field: "current",
    headerName: "当前剩余",
    sortable: false,
  },
  {
    field: "type",
    headerName: "类型",
    valueGetter: (value, row) => POINT_RECORD_TYPE_MAP[value],
  },
  {
    field: "detail",
    headerName: "详情",
    width: 200,
  },
  {
    field: "relation",
    headerName: "关联订单",
    width: 200,
  },
  {
    field: "createdAt",
    headerName: "时间",
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

// 贡献值明细列配置（type: 1=增加, 2=减少）
const CV_TYPE_MAP = {1: "增加", 2: "减少"};
const contributionColumns = [
  {field: "id", headerName: "ID", width: 80},
  {
    field: "amount",
    headerName: "变动数额",
    width: 120,
    renderCell: ({row}) => (
      <Typography
        variant="body2"
        color={parseFloat(row.amount) >= 0 ? "success.main" : "error.main"}
        sx={{lineHeight: "52px"}}
      >
        {row.amount}
      </Typography>
    ),
  },
  {field: "current", headerName: "当前剩余", width: 120},
  {
    field: "type",
    headerName: "类型",
    width: 80,
    valueGetter: (value) => CV_TYPE_MAP[value] || value,
  },
  {field: "detail", headerName: "详情", width: 220},
  {field: "relation", headerName: "关联单号", width: 200},
  {
    field: "createdAt",
    headerName: "时间",
    width: 180,
    valueGetter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];
const balanceDhqColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "type",
    headerName: "类型",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => BALANCE_RECORD_TYPE_MAP[value],
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];
const balanceColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "type",
    headerName: "类型",
    // description: 'This column has a value getter and is not sortable.',
    // width: 160,
    valueGetter: (value, row) => BALANCE_RECORD_TYPE_MAP[value],
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const xfqV2BakColumns = [
  {field: "id", headerName: "ID", width: 80},
  {
    field: "amount",
    headerName: "变动数额",
    width: 100,
    renderCell: ({row}) => (
      <Typography
        variant="body2"
        color={parseFloat(row.amount) >= 0 ? "success.main" : "error.main"}
        sx={{ lineHeight: "52px" }}
      >
        {row.amount}
      </Typography>
    ),
  },
  {field: "current", headerName: "当前剩余", width: 100},
  {
    field: "type",
    headerName: "类型",
    width: 120,
    valueGetter: (value) => XFQ_RECORD_TYPE_MAP[value] || value,
  },
  {field: "detail", headerName: "详情", width: 180},
  {field: "relation", headerName: "关联订单", width: 200},
  {
    field: "isMer",
    headerName: "账户类型",
    width: 90,
    valueGetter: (value) => value ? "商家" : "用户",
  },
  {
    field: "createdAt",
    headerName: "时间",
    width: 180,
    valueGetter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const txqColumns = [
  {field: "id", headerName: "ID", width: 90},
  // {
  //   field: "orderSN",
  //   headerName: "订单编号",
  //   width: 200,
  // },
  {
    field: "amount",
    headerName: "数额",
    // type: "number",
    // width: 150,
  },
  {
    field: "current",
    headerName: "当前剩余",
    // type: "number",
    // width: 110,
    sortable: false,
  },
  {
    field: "detail",
    headerName: "详情",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "relation",
    headerName: "关联订单",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    // valueGetter: (value, row) => `${parseFloat(Math.round((value / row.totalPrice) * 100))}%`,
  },
  {
    field: "createdAt",
    headerName: "时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const parentColumns = [
  {
    field: "parent",
    headerName: "当前推荐人",
    // description: 'This column has a value getter and is not sortable.',
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row.parent || {})} enablePreview={false}/>;
    },
  },
  {
    field: "prevParent",
    headerName: "之前推荐人",
    // description: 'This column has a value getter and is not sortable.',
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row.prevParent || {})} enablePreview={false}/>;
    },
  },
  {
    field: "operator",
    headerName: "操作人",
    // description: 'This column has a value getter and is not sortable.',
    width: 120,
  },
  {
    field: "createdAt",
    headerName: "操作时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const childListColumns = [
  {
    field: "uid",
    headerName: "被推荐人",
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row || {})} enablePreview={false}/>;
    },
  },
  {field: "mobile", headerName: "手机号", width: 120},
  {field: "jytId", headerName: "金银通ID", width: 100},
  {field: "income", headerName: "产出金豆", width: 100},
  {field: "coin_recharge", headerName: "充值金豆", width: 100},
  {field: "equityValue", headerName: "权益值", width: 100},
  {field: "directValue", headerName: "直推业绩", width: 100},
  {
    field: "createdAt",
    headerName: "注册时间",
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const parentListColumns = [
  {
    field: "idx",
    headerName: "层级",
    width: 100,
    renderCell: ({row, api}) => {
      // console.log(api.getRowIndexRelativeToVisibleRows(row.uid))
      return `第${api.getRowIndexRelativeToVisibleRows(row.uid) + 1}级`;
    },
  },
  {
    field: "uid",
    headerName: "推荐人",
    width: 220,
    renderCell: ({row}) => {
      return <UserInfo {...(row || {})} enablePreview={false}/>;
    },
  },
  {field: "mobile", headerName: "手机号", width: 120},
  // { field: "performance", headerName: "个人业绩", width: 100 },
  // { field: "teamPerformance", headerName: "团队业绩", width: 120 },
  {field: "childCount", headerName: "直推人数", width: 100},
  {field: "descendants", headerName: "团队人数", width: 120},
  {
    field: "createdAt",
    headerName: "注册时间",
    // description: 'This column has a value getter and is not sortable.',
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

const promotedMerchantColumns = [
  {
    field: "mid",
    headerName: "ID",
    width: 120,
  },
  {
    field: "name",
    headerName: "名称",
    width: 290,
  },
  {
    field: "logo",
    headerName: "logo",
    // description: 'This column has a value getter and is not sortable.',
    width: 190,
    renderCell: ({row}) => {
      // if (!row.merchant) return null;
      return <CellImage src={row.logo}/>;
      // return <MerchantInfo {...row.merchant} enablePreview={false} />;
    },
  },
  {
    field: "pointsGained",
    headerName: "累计让利",
    width: 120,
  },
  {
    field: "createdAt",
    headerName: "入驻时间",
    width: 200,
    valueGetter: (value, row) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

// 金银通明细列配置（充值金豆/产出金豆/权益值/直推业绩共用）
const JYT_RECORD_TYPE_MAP = {1: "增加", 2: "减少", 3: "冻结增加"};
const jytRecordColumns = [
  {field: "id", headerName: "ID", width: 80},
  {
    field: "type",
    headerName: "类型",
    width: 90,
    valueGetter: (value) => JYT_RECORD_TYPE_MAP[value] || value,
  },
  {
    field: "amount",
    headerName: "变动数额",
    width: 110,
    renderCell: ({row}) => (
      <Typography
        variant="body2"
        color={parseFloat(row.amount) >= 0 ? "success.main" : "error.main"}
        sx={{lineHeight: "52px"}}
      >
        {row.amount}
      </Typography>
    ),
  },
  {field: "before_balance", headerName: "变动前余额", width: 120},
  {field: "after_balance", headerName: "变动后余额", width: 120},
  {field: "remark", headerName: "备注", width: 220},
  {
    field: "create_time",
    headerName: "时间",
    width: 180,
    valueGetter: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
  },
];

// 二级Tab分组配置
const TAB_GROUPS = {
  "用户信息": {subs: []},
  "平台资产": {subs: ["权益金明细", "消费券明细", "消费券历史快照", "兑换券明细", "贡献值明细", "拼团贡献值明细"]},
  "金银通": {subs: ["充值金豆明细", "产出金豆明细", "权益值明细", "直推业绩明细", "直推下级", "上级直线"]},
  "订单": {subs: ["商城订单", "线下订单"]},
  "社交关系": {subs: ["推荐人变更记录", "推荐人关系", "推广粉丝"]},
};
// 条件Tab（需根据用户数据决定是否显示）
const CONDITIONAL_TABS = {"名下商家": "miniAdmins", "推广商家": "tiejun"};

export default function UserInfoDrawer({nickname, avatar, mobile, uid, open, onClose}) {
  const [groupValue, setGroupValue] = useState("用户信息");
  const [subValue, setSubValue] = useState("");

  const [totalCommission, setTotalCommission] = useState(0);
  const [info, setInfo] = useState({});
  const [orders, setOrders] = useState([]);
  const [storeOrders, setStoreOrders] = useState([]);
  const [pointRecordsV2, setPointRecordsV2] = useState([]);
  const [pointRecords, setPointRecords] = useState([]);
  const [xfqRecordsV2, setXfqRecordsV2] = useState([]);
  const [xfqV2BakRecords, setXfqV2BakRecords] = useState([]);
  const [xfqV2BakHistoryRecords, setXfqV2BakHistoryRecords] = useState([]);
  const [dhqRecordsV2, setDhqRecordsV2] = useState([]);
  const [xfqRecords, setXfqRecords] = useState([]);
  const [txqRecords, setTxqRecords] = useState([]);
  const [contributionRecords, setContributionRecords] = useState([]);
  const [pintuanContributionRecords, setPintuanContributionRecords] = useState([]);
  const [parentRecords, setParentRecords] = useState([]);
  const [childList, setChildList] = useState([]);
  const [merchants, setMerchants] = useState([]); // 名下的
  const [promotedMerchants, setPromotedMerchants] = useState([]);
  const [parentList, setParentList] = useState([]);
  const [goldVipStatus, setGoldVipStatus] = useState(null);

  // 金银通明细
  const [coinRechargeRecords, setCoinRechargeRecords] = useState([]);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [equityValueRecords, setEquityValueRecords] = useState([]);
  const [directValueRecords, setDirectValueRecords] = useState([]);
  const [referralTree, setReferralTree] = useState(null);

  const filter = {
    page: 0,
    size: 10,
  };

  // 根据子Tab名称触发对应数据加载
  const loadSubTabData = (sub) => {
    switch (sub) {
      case "商城订单": getStoreOrders(); break;
      case "线下订单": getOrders(); break;
      case "权益金明细": getXfqRecords(2); break;
      case "消费券明细": getXfqV2BakRecords(); break;
      case "消费券历史快照": getXfqV2BakHistoryRecords(); break;
      case "兑换券明细": getdhqV2List(2); break;
      case "贡献值明细": getContributionRecords(2); break;
      case "拼团贡献值明细": getContributionRecords(3); break;
      case "充值金豆明细": getJytRecords('coinRecharge'); break;
      case "产出金豆明细": getJytRecords('income'); break;
      case "权益值明细": getJytRecords('equity'); break;
      case "直推业绩明细": getJytRecords('direct'); break;
      case "直推下级": getReferralTree(); break;
      case "上级直线": getReferralTree(); break;
      case "推荐人变更记录": getParentRecords(); break;
      case "名下商家": getMyMerchants(); break;
      case "推广商家": getPromotedMerchants(); break;
      case "推广粉丝": getChildList(); break;
      case "推荐人关系": getParentList(); break;
      default: break;
    }
  };

  const handleGroupChange = (event, newGroup) => {
    setGroupValue(newGroup);
    const subs = TAB_GROUPS[newGroup]?.subs || [];
    if (subs.length > 0) {
      setSubValue(subs[0]);
      loadSubTabData(subs[0]);
    } else {
      setSubValue("");
    }
  };

  const handleSubChange = (event, newSub) => {
    setSubValue(newSub);
    loadSubTabData(newSub);
  };

  useEffect(() => {
    if (!uid || !open) return;
    getUserInfo();
    getGoldVipStatus();
    // getTotalCommission();
  }, [uid, open]);

  async function getTotalCommission() {
    const {data} = await asyncRequest({
      method: "get",
      path: `/user/commission/${uid}`,
    });

    setTotalCommission(data);
  }

  async function getUserInfo() {
    const {data} = await asyncRequest({
      method: "get",
      path: `/user/detail/${uid}`,
    });
    console.log("user info:", data);
    setInfo(data);
  }

  async function getGoldVipStatus() {
    try {
      const {data} = await asyncRequest({
        path: "/user/goldVipStatus",
        data: {uid},
      });
      setGoldVipStatus(data);
    } catch (error) {
      console.log("getGoldVipStatus error:", error);
      setGoldVipStatus(null);
    }
  }

  async function getOrders() {
    const {data} = await asyncRequest({
      path: "/payment/list",
      data: {
        uid,
        status: PAYMENT_STATUS.PAYED,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setOrders(data);
  }

  async function getStoreOrders() {
    const {data} = await asyncRequest({
      path: "/store/order/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
        status: -1,
      },
    });

    setStoreOrders(
      data.map((d) => ({
        ...d,
        createdAt: dayjs(d.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        payTime: d.payTime ? dayjs(d.payTime).format("YYYY-MM-DD HH:mm:ss") : d.payTime,
        productData: JSON.parse(d.productData) || [],
      }))
    );
  }

  async function getdhqV2List(version) {
    const {data} = await asyncRequest({
      path: "/finance/dhqV2/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
        version,
      },
    });

    setDhqRecordsV2(data);
  }

  async function getPointRecords(version) {
    const {data} = await asyncRequest({
      path: "/point/record/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
        version,
      },
    });

    if (version == 2) {
      setPointRecordsV2(data);
      return;
    }

    setPointRecords(data);
  }

  async function getXfqV2BakRecords() {
    const {data} = await asyncRequest({
      path: "/finance/xfq-v2-bak/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });
    setXfqV2BakRecords(data);
  }

  async function getXfqV2BakHistoryRecords() {
    const {data} = await asyncRequest({
      path: "/finance/xfq-v2-bak0302/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });
    setXfqV2BakHistoryRecords(data);
  }

  async function getXfqRecords(version) {
    const {data} = await asyncRequest({
      path: "/finance/xfq/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
        version,
      },
    });

    if (version == 2) {
      setXfqRecordsV2(data);
      return;
    }

    setXfqRecords(data);
  }

  async function getContributionRecords(fundPoolId) {
    const {data} = await asyncRequest({
      path: "/contribution-value/record/list",
      data: {
        uid,
        fundPoolId,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });
    if (fundPoolId === 3) {
      setPintuanContributionRecords(data);
    } else {
      setContributionRecords(data);
    }
  }

  async function getTxqRecords() {
    const {data} = await asyncRequest({
      path: "/finance/txq/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setTxqRecords(data);
  }

  async function getParentRecords() {
    const {data} = await asyncRequest({
      path: "/user/parent/record/list",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setParentRecords(data);
  }

  async function getMyMerchants() {
    const {data} = await asyncRequest({
      path: "/merchant/getByAdmin",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setMerchants(data);
  }

  async function getChildList() {
    const {data} = await asyncRequest({
      path: "/user/childList",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setChildList(data);
  }

  async function getParentList() {
    const {data} = await asyncRequest({
      path: "/user/parentHierarchy",
      data: {
        uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setParentList(data);
  }

  async function getPromotedMerchants() {
    const {data} = await asyncRequest({
      path: "/merchant/list",
      data: {
        uid,
        pid: info.uid,
        page: 0,
        size: 100000, // 导出需要，放开限制
      },
    });

    setPromotedMerchants(data);
  }

  // ========== 明细导出功能 ==========
  
  // 通用导出函数
  const exportToExcel = (data, columns, fileName, includeRemark = true) => {
    // 构建表头
    const headers = [];
    const keys = [];
    
    columns.forEach(col => {
      if (col.field && col.headerName) {
        headers.push(col.headerName);
        keys.push(col.field);
      }
    });
    
    // 如果有remark字段，添加备注列
    const hasRemark = data.length > 0 && data[0].remark !== undefined;
    if (includeRemark && hasRemark) {
      headers.push('备注');
      keys.push('remark');
    }
    
    // 转换数据
    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const header = alphabets.slice(0, headers.length);
    
    const excelData = [];
    excelData[0] = {};
    for (let i = 0; i < header.length; i++) {
      const alphabet = header[i];
      excelData[0][alphabet] = headers[i];
      
      for (let j = 0; j < data.length; j++) {
        if (!excelData[j + 1]) {
          excelData[j + 1] = {};
        }
        
        const key = keys[i];
        let value = data[j][key];
        
        // 处理特殊字段格式化
        if (key === 'createdAt' || key === 'time') {
          value = value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '';
        } else if (key === 'type') {
          // 根据列的valueGetter处理
          const col = columns.find(c => c.field === 'type');
          if (col && col.valueGetter) {
            value = col.valueGetter(value, data[j]);
          }
        } else if (key === 'isMer') {
          value = value ? '商家' : '用户';
        }
        
        excelData[j + 1][alphabet] = value !== undefined && value !== null ? value : '';
      }
    }
    
    const ws = XLSX.utils.json_to_sheet(excelData, { header, skipHeader: true });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, fileName);
    
    const now = moment();
    const fullFileName = `${fileName}_${now.format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(workbook, fullFileName);
  };

  // 权益金明细导出
  const exportXfqRecords = () => {
    exportToExcel(xfqRecordsV2, balanceColumns, '权益金明细');
  };

  // 消费券明细导出
  const exportXfqV2BakRecords = () => {
    exportToExcel(xfqV2BakRecords, xfqV2BakColumns, '消费券明细');
  };

  // 消费券历史快照导出
  const exportXfqV2BakHistoryRecords = () => {
    exportToExcel(xfqV2BakHistoryRecords, xfqV2BakColumns, '消费券历史快照');
  };

  // 兑换券明细导出
  const exportDhqV2Records = () => {
    exportToExcel(dhqRecordsV2, balanceDhqColumns, '兑换券明细');
  };

  // 贡献值明细导出
  const exportContributionRecords = () => {
    exportToExcel(contributionRecords, contributionColumns, '贡献值明细');
  };

  // 拼团贡献值明细导出
  const exportPintuanContributionRecords = () => {
    exportToExcel(pintuanContributionRecords, contributionColumns, '拼团贡献值明细');
  };

  // 商城订单导出
  const exportStoreOrders = () => {
    // 商城订单需要特殊处理productData和status字段
    const exportData = storeOrders.map(order => {
      // 处理商品信息
      let productInfo = '';
      if (order.productData && Array.isArray(order.productData)) {
        productInfo = order.productData.map(p => `${p.name} x${p.count}`).join('; ');
      }
      
      return {
        ...order,
        uid: order.uid || uid, // 填充购买人UID
        productData: productInfo, // 转换为文本
        status: ConvertUtil.getOrderStatusText(order.status), // 转换为文本
      };
    });
    
    exportToExcel(exportData, storeOrderColumns, '商城订单', false);
  };

  // 线下订单导出
  const exportOfflineOrders = () => {
    // 线下订单需要特殊处理valueGetter字段
    const exportData = orders.map(order => {
      return {
        ...order,
        uid: order.uid || uid, // 填充购买人UID
        totalPrice: `￥${order.totalPrice}`,
        rangli: `￥${order.rangli}(${new Decimal(order.rangli).div(order.totalPrice).mul(100).toFixed(0)}%)`,
        detail: `￥${new Decimal(order.totalPrice).sub(order.rangli).toNumber()}`,
        sid: `￥${order.payPrice} | ${order.deductionXfqV2} | ${order.deductionPointsV2}`,
        payMethod: order.payPrice == 0 ? '消费券支付' : PAY_METHOD_MAP[order.payMethod],
        merchant: order.merchant ? order.merchant.name : '',
        createdAt: order.createdAt ? moment(order.createdAt).format('YYYY-MM-DD HH:mm') : '',
        payTime: order.payTime ? moment(order.payTime).format('YYYY-MM-DD HH:mm:ss') : '',
      };
    });
    
    exportToExcel(exportData, orderColumns, '线下订单', false);
  };

  async function getJytRecords(type) {
    const {data} = await asyncRequest({
      path: "/jingxi/jyt-user/jyt-records",
      data: {uid, type, page: 0, size: 100000}, // 导出需要，放开限制
    });
    switch (type) {
      case 'coinRecharge': setCoinRechargeRecords(data || []); break;
      case 'income': setIncomeRecords(data || []); break;
      case 'equity': setEquityValueRecords(data || []); break;
      case 'direct': setDirectValueRecords(data || []); break;
      default: break;
    }
  }

  async function getReferralTree() {
    const {data} = await asyncRequest({
      path: "/jingxi/jyt-user/referral-tree",
      data: {uid, depth: 1},
    });
    setReferralTree(data);
  }

  async function loadReferralChildren(jytId, depth = 1) {
    // depth=-1 表示收回，清空该节点 children
    if (depth === -1) {
      setReferralTree((prev) => {
        if (!prev) return prev;
        const clearChildren = (nodes) => {
          for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].id === jytId) {
              nodes[i] = {...nodes[i], children: []};
              return true;
            }
            if (nodes[i].children && nodes[i].children.length > 0) {
              if (clearChildren(nodes[i].children)) return true;
            }
          }
          return false;
        };
        const newChildren = [...prev.children];
        clearChildren(newChildren);
        return {...prev, children: newChildren};
      });
      return;
    }

    const {data} = await asyncRequest({
      path: "/jingxi/jyt-user/referral-tree/children",
      data: {jytId, depth},
    });
    // 将返回的子节点树插入到 referralTree 中对应节点
    setReferralTree((prev) => {
      if (!prev) return prev;
      const insertChildren = (nodes) => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].id === jytId) {
            nodes[i] = {...nodes[i], children: data || []};
            return true;
          }
          if (nodes[i].children && nodes[i].children.length > 0) {
            if (insertChildren(nodes[i].children)) return true;
          }
        }
        return false;
      };
      const newChildren = [...prev.children];
      insertChildren(newChildren);
      return {...prev, children: newChildren};
    });
  }

  return (
    <>
      {open && (
        <Drawer anchor="right" open onClose={onClose}>
          <Box sx={{width: "90vw"}} p={3}>
            <Stack alignContent="space-between" direction="row">
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  // cursor: "pointer",
                  width: "100%",
                }}
                alignSelf={1}
              >
                <Avatar alt={nickname} src={`${IMG_BASE_URL}${avatar}?w=60`}/>
                <Stack>
                  <Typography variant="subtitle2" noWrap>
                    {nickname || mobile}
                  </Typography>
                  <Typography variant="subtitle2" noWrap>
                    {uid}
                  </Typography>
                </Stack>
                {info.tiejun ? <Chip label={[TIEJUN_LEVEL_MAP[info.tiejun.level]]} color="primary"/> : null}
                {info.outPlatformCode && info.outPlatformCode == 'WJT' ?
                  <Chip label={'文金通'} color="primary"/> : null}
              </Stack>
              <IconButton onClick={onClose}>
                <CloseOutlined/>
              </IconButton>
            </Stack>

            {/* <Grid container mt={3}>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>2.0消费券</Typography>
                  <Typography>{info.xfqV2 || 0}</Typography>
                </Stack>
              </Grid>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>2.0绿积分</Typography>
                  <Typography>{info.pointsV2 || 0}</Typography>
                </Stack>
              </Grid>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>2.0兑换券</Typography>
                  <Typography>{info.dhqV2 || 0}</Typography>
                </Stack>
              </Grid>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>锁仓积分</Typography>
                  <Typography>{info.pointsGreenC || 0}</Typography>
                </Stack>
              </Grid>
              <Grid item xs>
                <Stack gap={1} alignItems="center">
                  <Typography>活动锁仓积分</Typography>
                  <Typography>{info.lockPointsV2 || 0}</Typography>
                </Stack>
              </Grid>
            </Grid> */}

            <Box sx={{width: "100%"}}>
              {/* 一级Tab：大类分组 */}
              <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                <Tabs value={groupValue} onChange={handleGroupChange} variant="scrollable" scrollButtons="auto">
                  {Object.keys(TAB_GROUPS).map((g) => (
                    <Tab key={g} label={g} value={g}/>
                  ))}
                  {!!info.miniAdmins && <Tab label="名下商家" value="名下商家"/>}
                  {!!info.tiejun && <Tab label="推广商家" value="推广商家"/>}
                </Tabs>
              </Box>

              {/* 二级Tab：子分类（用户信息/名下商家/推广商家无二级） */}
              {groupValue !== "用户信息" && groupValue !== "名下商家" && groupValue !== "推广商家" && (
                <Box sx={{borderBottom: 1, borderColor: "divider", bgcolor: 'grey.50'}}>
                  <Tabs
                    value={subValue}
                    onChange={handleSubChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{sx: {height: 2}}}
                    sx={{minHeight: 36, '& .MuiTab-root': {minHeight: 36, py: 0.5, fontSize: '0.8rem'}}}
                  >
                    {(TAB_GROUPS[groupValue]?.subs || []).map((s) => (
                      <Tab key={s} label={s} value={s}/>
                    ))}
                  </Tabs>
                </Box>
              )}

              <Box sx={{height: 700, width: "100%"}}>
                {/* ====== 用户信息 ====== */}
                {groupValue === "用户信息" && (
                  <Stack p={2}>
                    <SectionHeader title="基础信息"/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户ID：</Typography>
                          <Typography>{info.uid}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户昵称：</Typography>
                          <Typography>{info.nickname}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>手机号码：</Typography>
                          <Typography>{info.mobile}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="用户概况" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户状态：</Typography>
                          <Typography>{USER_STATUS_MAP[info.status]}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>用户等级：</Typography>
                          <Typography>{USER_LEVEL_MAP[info.level]}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>联创：</Typography>
                          <Typography>
                            {info.isHehuo ? "合伙人" : ""} {info.isDaiyan ? "代言人" : ""}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>渠道等级：</Typography>
                          <Typography>{ROLE_LEVEL_MAP[info.roleLevel]}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>注册时间：</Typography>
                          <Typography>{moment(info.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="推荐关系" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>推荐人：</Typography>
                          {info.fromMer ? <MerchantInfo {...info.parent} enablePreview={false}/> :
                            <UserInfo {...info.parent} enablePreview={false}/>}
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="账户详情" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>权益金：</Typography>
                          <Typography>{info.xfqV2 || 0}({info.xfqLocked || 0})</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>消费券：</Typography>
                          <Typography>{info.xfqV2Bak || 0}</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>兑换券：</Typography>
                          <Typography>{info.dhqV2 || 0}</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 120}}>贡献值：</Typography>
                          <Typography>{info.pintuanContributionValue || 0}({info.pintuanConvertibleQuota || 0}%)</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="金银通VIP状态" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{width: 120}}>同步状态：</Typography>
                          {goldVipStatus ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{fontSize: 12}} style={{
                                color: goldVipStatus.matchStatus === 'MATCH' ? '#4caf50'
                                  : goldVipStatus.matchStatus === 'MISMATCH' ? '#f44336'
                                    : '#bdbdbd'
                              }}/>
                              <Typography variant="body2" style={{
                                color: goldVipStatus.matchStatus === 'MATCH' ? '#4caf50'
                                  : goldVipStatus.matchStatus === 'MISMATCH' ? '#f44336'
                                    : '#9e9e9e'
                              }}>
                                {goldVipStatus.matchStatus === 'MATCH' ? '两端到期时间一致'
                                  : goldVipStatus.matchStatus === 'MISMATCH' ? '两端到期时间不一致'
                                    : '均未开通'}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">加载中...</Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 120}}>系统VIP到期：</Typography>
                          {goldVipStatus && goldVipStatus.local.isValid ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{fontSize: 10}} style={{color: '#4caf50'}}/>
                              <Typography variant="body2">{goldVipStatus.local.endDate}</Typography>
                            </Stack>
                          ) : (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{fontSize: 10}} style={{color: '#bdbdbd'}}/>
                              <Typography variant="body2" color="text.secondary">未开通</Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 120}}>金银通机器人：</Typography>
                          {goldVipStatus && goldVipStatus.remote.enabled ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{fontSize: 10}} style={{color: '#4caf50'}}/>
                              <Typography variant="body2">{goldVipStatus.remote.expireDate}</Typography>
                            </Stack>
                          ) : goldVipStatus && goldVipStatus.remote.error ? (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{fontSize: 10}} style={{color: '#ff9800'}}/>
                              <Typography variant="body2" color="error">查询失败</Typography>
                            </Stack>
                          ) : (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <CircleIcon sx={{fontSize: 10}} style={{color: '#bdbdbd'}}/>
                              <Typography variant="body2" color="text.secondary">未开通</Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="平台消费" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>线上现金：</Typography>
                          <Typography>{info.onLineConsumption || 0}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 100}}>线上消费券：</Typography>
                          <Typography>{info.onLineConsumptionXfq || 0}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>线下现金：</Typography>
                          <Typography>{info.offLineConsumption || 0}</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 100}}>线下消费券：</Typography>
                          <Typography>{info.offLineConsumptionXfq || 0}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="其他" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 130}}>文金通锁仓积分：</Typography>
                          <Typography>{info.lockPointsWjt || 0}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>

                    <SectionHeader title="1.0数据" mt={2}/>
                    <Grid container py={1} rowGap={1}>
                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>消费券：</Typography>
                          <Typography>{info.xfq || 0}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={6}>
                        <Stack gap={1} alignItems="center" direction="row">
                          <Typography sx={{width: 80}}>绿积分：</Typography>
                          <Typography>{info.points || 0}</Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                )}
                {subValue === "线下订单" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportOfflineOrders} sx={{width: 'fit-content'}}>
                      导出订单
                    </Button>
                    <DataGrid
                      rows={orders}
                      columns={orderColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      // checkboxSelection
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}
                {subValue === "商城订单" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportStoreOrders} sx={{width: 'fit-content'}}>
                      导出订单
                    </Button>
                    <DataGrid
                      rows={storeOrders}
                      columns={storeOrderColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      // checkboxSelection
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}
                {/* 权益金明细 */}
                {subValue === "权益金明细" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportXfqRecords} sx={{width: 'fit-content'}}>
                      导出明细
                    </Button>
                    <DataGrid
                      rows={xfqRecordsV2}
                      columns={balanceColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      // checkboxSelection
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}
                {/* 兑换券明细 */}
                {subValue === "兑换券明细" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportDhqV2Records} sx={{width: 'fit-content'}}>
                      导出明细
                    </Button>
                    <DataGrid
                      rows={dhqRecordsV2}
                      columns={balanceDhqColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      // checkboxSelection
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}
                {/* 消费券明细 */}
                {subValue === "消费券明细" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportXfqV2BakRecords} sx={{width: 'fit-content'}}>
                      导出明细
                    </Button>
                    <DataGrid
                      rows={xfqV2BakRecords}
                      columns={xfqV2BakColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      // checkboxSelection
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}

                {/* 消费券历史快照 */}
                {subValue === "消费券历史快照" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportXfqV2BakHistoryRecords} sx={{width: 'fit-content'}}>
                      导出明细
                    </Button>
                    <DataGrid
                      rows={xfqV2BakHistoryRecords}
                      columns={xfqV2BakColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      // checkboxSelection
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}

                {/* 贡献值明细 */}
                {subValue === "贡献值明细" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportContributionRecords} sx={{width: 'fit-content'}}>
                      导出明细
                    </Button>
                    <DataGrid
                      rows={contributionRecords}
                      columns={contributionColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}

                {/* 拼团贡献值明细 */}
                {subValue === "拼团贡献值明细" && (
                  <Stack spacing={2}>
                    <Button variant="contained" size="small" onClick={exportPintuanContributionRecords} sx={{width: 'fit-content'}}>
                      导出明细
                    </Button>
                    <DataGrid
                      rows={pintuanContributionRecords}
                      columns={contributionColumns}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      disableColumnFilter
                      disableColumnSorting
                      pageSizeOptions={[5]}
                      disableRowSelectionOnClick
                    />
                  </Stack>
                )}

                {/* 推荐人关系 */}
                {subValue === "推荐人关系" && (
                  <DataGrid
                    rows={parentList}
                    columns={parentListColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    getRowId={(row) => row.uid}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推荐人 */}
                {subValue === "推荐人变更记录" && (
                  <DataGrid
                    rows={parentRecords}
                    columns={parentColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推广粉丝 */}
                {subValue === "推广粉丝" && (
                  <DataGrid
                    rows={childList}
                    columns={childListColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    getRowId={(row) => row.uid}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 名下商家 */}
                {groupValue === "名下商家" && (
                  <DataGrid
                    getRowId={(r) => r.mid}
                    rows={merchants}
                    columns={promotedMerchantColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 推广商家 */}
                {groupValue === "推广商家" && (
                  <DataGrid
                    getRowId={(r) => r.mid}
                    rows={promotedMerchants}
                    columns={promotedMerchantColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    // checkboxSelection
                    disableRowSelectionOnClick
                  />
                )}

                {/* 充值金豆明细 */}
                {subValue === "充值金豆明细" && (
                  <DataGrid
                    rows={coinRechargeRecords}
                    columns={jytRecordColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    disableRowSelectionOnClick
                  />
                )}

                {/* 产出金豆明细 */}
                {subValue === "产出金豆明细" && (
                  <DataGrid
                    rows={incomeRecords}
                    columns={jytRecordColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    disableRowSelectionOnClick
                  />
                )}

                {/* 权益值明细 */}
                {subValue === "权益值明细" && (
                  <DataGrid
                    rows={equityValueRecords}
                    columns={jytRecordColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    disableRowSelectionOnClick
                  />
                )}

                {/* 直推业绩明细 */}
                {subValue === "直推业绩明细" && (
                  <DataGrid
                    rows={directValueRecords}
                    columns={jytRecordColumns}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    disableColumnFilter
                    disableColumnSorting
                    pageSizeOptions={[5]}
                    disableRowSelectionOnClick
                  />
                )}

                {/* 直推下级 */}
                {subValue === "直推下级" && (
                  <ReferralTree
                    data={referralTree}
                    onLoadChildren={loadReferralChildren}
                  />
                )}

                {/* 上级直线 */}
                {subValue === "上级直线" && (
                  <AncestorLine
                    data={referralTree}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
}
